import { CheckIcon, InfoIcon, WarningCircleIcon } from '@phosphor-icons/react/dist/ssr';

import { TooltipWrapper } from '@/ui/TooltipWrapper';

import BadgeElement from '../shared/badgeElement/BadgeElement';
import { ButtonElement } from '../shared/buttonElement/ButtonElement';
import { LoaderElement } from '../shared/loaderElement/LoaderElement';
import { SectionElement } from '../shared/sectionElement/SectionElement';
import type { CurrentPlansElementProps } from './CurrentPlansElement.types';

export function CurrentPlansElement(props: CurrentPlansElementProps) {
  const {
    translations,
    children,
    element,
    isLoading,
    showEmptyState,
    onClickCancelPlan,
    onClickChangePlan,
    onClickUnpausePlan,
  } = props;
  const { data } = element.attrs || {};

  if (isLoading) {
    return (
      <SectionElement title={translations?.['billing.current_plan.title']}>
        <LoaderElement count={4} />
      </SectionElement>
    );
  }

  if (showEmptyState) {
    return (
      <SectionElement
        title={translations?.['billing.current_plan.title']}
        subTitle={translations?.['billing.summary.empty_state']}
      >
        {null}
      </SectionElement>
    );
  }

  const rightChildren = data?.statusBadge && (
    <TooltipWrapper
      portalId="current-plans-container"
      tooltipText={data.statusBadge.tooltipText || ''}
      shouldMount={!!data.statusBadge.tooltipText}
      isTriggerAsChild={false}
    >
      <BadgeElement type={data.statusBadge.type} className="capitalize">
        {data.statusBadge.text}
      </BadgeElement>
    </TooltipWrapper>
  );

  const handleClickCancelPlan = (planId?: string) => () => onClickCancelPlan?.(planId);
  const handleClickChangePlan = (planId?: string) => () => onClickChangePlan?.(planId);
  const handleClickUnpausePlan = (planId?: string) => () => onClickUnpausePlan?.(planId);

  return (
    <SectionElement title={translations?.['billing.current_plan.title']} rightChildren={rightChildren}>
      <div className="flex flex-col gap-y-4" id="current-plans-container">
        {data?.overlapWarning && (
          <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4">
            <WarningCircleIcon weight="fill" width={20} height={20} className="text-red-500 shrink-0 mt-0.5" />
            <p className="text-sm leading-5 font-normal text-red-900">{data.overlapWarning}</p>
          </div>
        )}
        {data?.plans?.map((plan) => {
          const hasFeatures = Array.isArray(plan.features) && plan.features.length > 0;
          const hasActions = plan.canDowngrade || plan.canChangePlan || plan.canUnpausePlan;

          return (
            <div key={plan.name} className="rounded-md border border-gray-200 overflow-hidden">
              <div className="flex flex-col gap-2 p-4 bg-gray-50">
                <div className="flex items-center justify-between">
                  <span className="flex flex-col gap-2 sm:items-center sm:flex-row">
                    <p className="text-base leading-6 font-medium text-gray-900 capitalize">{plan.name}</p>
                    {plan.badgeType && !data?.statusBadge && (
                      <TooltipWrapper
                        portalId={`${plan.id}-status-tooltip`}
                        tooltipText={plan.tooltipText || ''}
                        shouldMount={!!plan.tooltipText}
                        isTriggerAsChild={false}
                      >
                        <BadgeElement type={plan.badgeType} className="capitalize w-fit">
                          {plan.status}
                        </BadgeElement>
                      </TooltipWrapper>
                    )}
                  </span>
                  <div className="flex flex-row gap-x-2 items-center">
                    {plan.price && !plan.groupManagedBy && (
                      <div className="flex flex-col gap-1 sm:flex-row sm:gap-4">
                        <p className="text-base leading-6 font-semibold text-gray-900">{plan.price}</p>
                        {plan.isDiscounted && (
                          <p className="text-base leading-6 font-semibold text-gray-500 line-through">
                            {plan.originalPrice}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {plan.groupManagedBy && (
                  <p className="text-sm leading-5 font-normal text-gray-500">{plan.groupManagedBy}</p>
                )}
                {plan.accessEndsAt && (
                  <p className="text-sm leading-5 font-medium text-gray-700">{plan.accessEndsAt}</p>
                )}
              </div>
              {hasFeatures || hasActions ? (
                <div className={'flex flex-col p-4 gap-3'}>
                  {hasFeatures && (
                    <ul className="flex flex-col gap-2">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-base">
                          <CheckIcon width={20} height={20} className="text-gray-700 p-1 grow-1 shrink-0" />
                          <p className="text-gray-700 text-sm font-normal leading-5">{feature}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                  {hasActions && (
                    <div className="flex flex-wrap gap-2 justify-start">
                      {plan.canDowngrade && (
                        <ButtonElement
                          disabled={plan.isDowngrading}
                          variant="default"
                          onClick={handleClickCancelPlan(plan.id)}
                        >
                          {translations?.['shared.cta.cancel_paid_subscription']}
                        </ButtonElement>
                      )}
                      {plan.canChangePlan && (
                        <ButtonElement
                          disabled={plan.isDowngrading}
                          variant="primary"
                          onClick={handleClickChangePlan(plan.id)}
                        >
                          {translations?.['billing.plan.change_plan']}
                        </ButtonElement>
                      )}
                      {plan.canUnpausePlan && (
                        <ButtonElement variant="primary" onClick={handleClickUnpausePlan(plan.id)}>
                          {translations?.['billing.plan.unpause_plan']}
                        </ButtonElement>
                      )}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          );
        })}
        {data?.revokedAccess && (
          <div className="flex items-start gap-3 rounded-md border border-indigo-200 bg-indigo-50 p-4">
            <InfoIcon weight="fill" width={20} height={20} className="text-indigo-500 shrink-0 mt-0.5" />
            <p className="text-sm leading-5 font-normal text-indigo-900">{data.revokedAccess}</p>
          </div>
        )}
        {children}
      </div>
    </SectionElement>
  );
}
